const WINDOWS_FORBIDDEN_CHARS_REGEX = /[\\/:*?"<>|]/g;
const ONEDRIVE_RESTRICTED_CHARS_REGEX = /[#%]/g;
const TRAILING_DOTS_AND_SPACES_REGEX = /[. ]+$/g;
const LEADING_DOTS_REGEX = /^\.+/g;

const RESERVED_WINDOWS_NAMES = new Set([
    'CON', 'PRN', 'AUX', 'NUL',
    'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
    'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
]);

const removeControlCharacters = (value) => Array.from(value)
    .filter((char) => {
        const codePoint = char.codePointAt(0);
        if (codePoint === undefined) return false;
        return codePoint >= 32 && codePoint !== 127;
    })
    .join('');

const toSafeFilePart = (value) => {
    let safe = String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036F]/g, '')
        ;

    safe = removeControlCharacters(safe)
        .replace(WINDOWS_FORBIDDEN_CHARS_REGEX, '')
        .replace(ONEDRIVE_RESTRICTED_CHARS_REGEX, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(LEADING_DOTS_REGEX, '')
        .replace(TRAILING_DOTS_AND_SPACES_REGEX, '');

    if (!safe) return '';

    // Prevent Windows/OneDrive reserved filenames.
    if (RESERVED_WINDOWS_NAMES.has(safe.toUpperCase())) {
        safe = `_${safe}`;
    }

    return safe;
};

const capitalizeNamePart = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/**
 * Build a safe PDF filename for patient measurements
 * @param {string|object} firstNameOrFullName - Patient first name, or full name (for backward compatibility)
 * @param {string|Date} lastNameOrCreatedAt - Patient last name, or createdAt date (for backward compatibility)
 * @param {Date} [createdAt] - Measurement creation date (when using separate first/last names)
 * @returns {string} - Safe filename like "Lastname Firstname YYYYMMDD.pdf"
 */
export const buildPdfFilename = (firstNameOrFullName, lastNameOrCreatedAt, createdAt) => {
    let firstName, lastName, measurementDate;

    // Check if called with new signature (firstName, lastName, createdAt) or old signature (fullName, createdAt)
    if (createdAt !== undefined) {
        // New signature: (firstName, lastName, createdAt)
        firstName = (firstNameOrFullName || 'Unknown').trim();
        lastName = (lastNameOrCreatedAt || 'Unknown').trim();
        measurementDate = createdAt ? new Date(createdAt) : new Date();
    } else {
        // Old signature: (fullName, createdAt) - parse fullName
        const rawPatientName = (firstNameOrFullName || '').trim();
        const patientNameParts = rawPatientName.split(/\s+/).filter(Boolean);

        if (patientNameParts.length === 1) {
            firstName = patientNameParts[0];
            lastName = 'Unknown';
        } else if (patientNameParts.length > 1) {
            firstName = patientNameParts[0];
            lastName = patientNameParts.slice(1).join(' ');
        } else {
            firstName = 'Unknown';
            lastName = 'Unknown';
        }

        measurementDate = lastNameOrCreatedAt ? new Date(lastNameOrCreatedAt) : new Date();
    }

    const year = measurementDate.getFullYear().toString();
    const month = (measurementDate.getMonth() + 1).toString().padStart(2, '0');
    const day = measurementDate.getDate().toString().padStart(2, '0');
    const yyyymmdd = `${year}${month}${day}`;

    const safeFirstName = toSafeFilePart(firstName) || 'Unknown';
    const safeLastNameNoSpaces = (
        toSafeFilePart(lastName)
            .split(/\s+/)
            .filter(Boolean)
            .map(capitalizeNamePart)
            .join('')
    ) || 'Unknown';

    return `${safeLastNameNoSpaces} ${safeFirstName} ${yyyymmdd}.pdf`;
};
